/**
 * CreatableCombobox
 * Combobox that allows selecting from existing options or creating new values.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ChevronDown } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { CreatableComboboxList } from './CreatableComboboxList';
import { useCreatableCombobox } from './useCreatableCombobox';
import { safeStr, type LabeledComboboxOption } from './creatable-combobox.helpers';

export type { LabeledComboboxOption } from './creatable-combobox.helpers';

export interface CreatableComboboxProps {
  options?: string[];
  labeledOptions?: LabeledComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  createLabel?: (value: string) => string;
  disabled?: boolean;
  className?: string;
  /** Sort options alphabetically by label (default); false preserves input order. */
  sortOptions?: boolean;
}

export const CreatableCombobox: FC<CreatableComboboxProps> = ({
  options = [],
  labeledOptions,
  value,
  onChange,
  placeholder = 'Select or type to create...',
  createLabel = (v) => `Create "${v}"`,
  disabled = false,
  className,
  sortOptions = true,
}) => {
  const {
    open,
    setOpen,
    inputValue,
    setInputValue,
    anchorRef,
    keepOpenIfInsideAnchor,
    labeledMode,
    filteredPlain,
    filteredLabeled,
    presetCount,
    canCreate,
    handleSelect,
    handleCreate,
    handleOpenChange,
  } = useCreatableCombobox({ options, labeledOptions, value, onChange, sortOptions });

  return (
    <Popover.Root modal={false} open={open} onOpenChange={handleOpenChange}>
      <Popover.Anchor asChild>
        <div ref={anchorRef} className={cn('relative', className)}>
          <input
            type="text"
            value={safeStr(inputValue)}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && open) {
                e.preventDefault();
                if (canCreate) handleCreate();
                else if (labeledMode && filteredLabeled[0])
                  handleSelect(safeStr(filteredLabeled[0].value));
                else if (!labeledMode && filteredPlain[0]) handleSelect(filteredPlain[0]);
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-9 text-sm',
              'ring-offset-background placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
            />
          </span>
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={keepOpenIfInsideAnchor}
          onFocusOutside={keepOpenIfInsideAnchor}
          className={cn(
            'z-[250] w-[var(--radix-popover-trigger-width)] min-w-[200px] max-w-[360px]',
            'rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg',
            'data-[state=open]:animate-in',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <CreatableComboboxList
            labeledMode={labeledMode}
            filteredPlain={filteredPlain}
            filteredLabeled={filteredLabeled}
            presetCount={presetCount}
            value={safeStr(value)}
            canCreate={canCreate}
            createLabel={createLabel}
            inputTrimmed={safeStr(inputValue).trim()}
            onSelect={handleSelect}
            onCreate={handleCreate}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default CreatableCombobox;
